import { useCallback, useEffect, useRef, useState } from 'react';

import { useToast } from '@/components/feedback/toast';
import { DEFAULT_CIRCLE_RADIUS_FT, type FieldCircle } from '@/lib/maps/circle';
import { popSnapshot, pushSnapshot } from '@/lib/maps/field-drawing';
import type { FieldTarget } from '@/lib/maps/hit-test';
import type { MapPoint } from '@/lib/maps/types';

import { checkCirclePlacement, checkPlacement, type PlacementCheck } from './check-placement';
import { useValidateBoundary } from './hooks';
import type { FieldOrigin, FieldShapeType } from './schemas';

/**
 * One slot for every marking refusal (113600). Re-opening a notification with the same key updates
 * it and restarts its timer instead of stacking a second — which is exactly the criterion, and
 * `useToast` already supports it, so there is no machinery here beyond the constant.
 */
export const MARKING_TOAST_KEY = 'field-mapping-marking';
const TOAST_DURATION_SECONDS = 5;

/**
 * How long a click is held before it becomes a **Point** — one double-click interval.
 *
 * Google fires `click` **before** `dblclick`, "sometimes once and sometimes twice", so a
 * double-click meant to zoom would otherwise leave a **Point** behind. Holding the mark for this
 * long and dropping it if `cancelPendingMarks` arrives is what makes "double-click zooms and marks
 * no Point" true. Each click gets its OWN timer rather than a shared debounce, so two corners marked
 * 200 ms apart both land.
 *
 * 250 ms is the platform double-click threshold, and it is invisible next to the round trip that
 * follows it.
 */
export const DOUBLE_CLICK_WINDOW_MS = 250;

/**
 * The **Point allowance**, keyed on where the shape came from — the client's mirror of the
 * backend's own `MAX_POINTS_BY_ORIGIN` (`field.constants.ts`), so the two sides cannot drift.
 *
 * 50 for a shape the Grower drew (113600; 113601's stray "30 point rows" line contradicts its own
 * sentence — grilling § design discrepancy 5 settles it at 50 for both typing and clicking), 200
 * for one imported from a shapefile (113610).
 *
 * The allowance is a property of the **origin**, not of the count: it is fixed for the session and
 * permanent for the Field's life, and it is **not** a high-water mark — deleting an imported
 * shape's Points below 50 does not demote it. That is why `origin` is a plain option below and
 * never enters the undo snapshot (grilling L7; 113599's D18 stands unamended).
 */
export const MAX_POINTS_BY_ORIGIN: Record<FieldOrigin, number> = { drawn: 50, imported: 200 };

/** The backend's own copy, mirrored so the two sides cannot drift (`FIELD_MAX_POINTS_EXCEEDED`
 *  / `maxPointsMessage`). */
export const maxPointsMessage = (origin: FieldOrigin) =>
  `You can mark a maximum of ${MAX_POINTS_BY_ORIGIN[origin]} points.`;

/** The **Field** minimum (113600). A shape below this encloses no area at all — `isDrawableRing` in
 *  `boundaries.ts` draws the same line, and `<MapPolygon>` starts filling at exactly this count. */
export const MIN_DRAWN_POINTS = 3;

/** The backend's own copy, mirrored so the two sides cannot drift (`FIELD_MIN_POINTS_REQUIRED`). */
export const MIN_POINTS_MESSAGE = `At least ${MIN_DRAWN_POINTS} points are required.`;

/**
 * `Save Mapping`'s own toast slot — deliberately **not** {@link MARKING_TOAST_KEY}. One stable key
 * per concern: a refused save and a refused mark are different things to be told, and sharing a key
 * would let one silently replace the other.
 */
export const SAVE_MAPPING_TOAST_KEY = 'field-mapping-save';

/**
 * 113600: "a click on the exact location of an existing **Point** creates nothing and assigns no
 * number." **Exact** equality, not a distance tolerance — two clicks at the same pixel and zoom
 * yield the same float pair from the SDK, and the same criteria explicitly accept tight spacing and
 * self-intersecting shapes, so there is no "near enough" case to catch. A pre-empt, never an
 * overrule: the server refuses the same click as `FIELD_DUPLICATE_POINT`.
 */
const isDuplicate = (candidate: MapPoint, points: readonly MapPoint[]) =>
  points.some((point) => point.lat === candidate.lat && point.lon === candidate.lon);

/**
 * The **committed shape** — and exactly what one entry of the undo stack holds. Three axes, and at
 * most ONE of the two geometry axes is ever non-empty: a Field is saved as a polygon or as a
 * circle, and `switchMode` clears both.
 *
 * `tool` lives here rather than in the screen because a **Shape mode** switch CLEARS the shape, so
 * reversing one has to put the tool back as well as the geometry. Holding all three in the same
 * snapshot is what makes that a single `pop()` instead of five remembered inverse commands
 * (ADR-0018, ticket 11).
 */
type ShapeState = {
  tool: FieldShapeType;
  points: MapPoint[];
  circle: FieldCircle | null;
};

type FieldPointsState = ShapeState & { history: ShapeState[] };

/**
 * Every commit site shares this: keep `current`'s whole shape on the stack, then apply the change.
 * One helper rather than one per axis, because the snapshot **is** the shape
 * (`const { history, ...shape }`) — so no commit site can record a partial one.
 */
function commit(current: FieldPointsState, next: Partial<ShapeState>): FieldPointsState {
  const { history, ...shape } = current;
  return { ...shape, ...next, history: pushSnapshot(history, shape) };
}

export type FieldPointsOptions = {
  /** The Field being edited, or `null` when creating. Required, never defaulted — see
   *  `checkPlacement`. */
  excludeFieldId: string | null;
  /**
   * The shape this session opens with. Read **once**, by the `useState` initializer — not resynced
   * on change, because a session's own marks must never be overwritten by a re-render. A future edit
   * route is its other consumer; here it is what lets the 50-**Point** ceiling be tested without
   * fifty round trips.
   */
  initialPoints?: readonly MapPoint[];
  /**
   * The circle this session opens with, read **once** by the `useState` initializer for the same
   * reason `initialPoints` is. Ticket 14's edit route is its only consumer: a Field saved in circle
   * **Shape mode** reopens on its own **Mid Point** and **Radius** rather than empty.
   */
  initialCircle?: FieldCircle | null;
  /**
   * The **Shape mode** the session opens in. Read **once**, by the `useState` initializer, for the
   * same reason `initialPoints` is: from here on the active mode is COMMITTED state that rides the
   * undo stack, so a prop that kept re-supplying it would fight every switch and every Undo.
   *
   * `'polygon'` on arrival (113600). Defaulted, unlike `excludeFieldId` — there is exactly one
   * source of truth for the mode now, so a default cannot silently disagree with a caller. Ticket
   * 14's edit route opens a circle Field in `'circle'`.
   */
  initialMode?: FieldShapeType;
  /**
   * Where the shape came from, and therefore its **Point allowance** (50 drawn / 200 imported —
   * {@link MAX_POINTS_BY_ORIGIN}).
   *
   * Unlike `initialPoints` / `initialCircle` / `initialMode` this is **NOT** read once into state:
   * it is a constant for the whole mapping session, so it stays a plain option and **never enters
   * the undo snapshot** (grilling L7; 113599's D18 stands unamended). That is what makes the
   * allowance survive a Shape mode switch and its Undo for free, and what makes it a property of
   * the origin rather than a high-water mark over the count.
   */
  origin?: FieldOrigin;

  /**
   * A **Mid Point** was just placed, and this is the circle that committed around it.
   *
   * The camera's cue, and it lives here because the camera is the *page*'s: this hook owns the
   * shape, `<FieldMappingScreen>` owns the adapter, and `circleArrivalZoom` is the rule between
   * them. Fired only for a placement that actually committed — never for a circle seeded by
   * `initialCircle`, restored by Undo, or committed by the panel's own Save, none of which are a
   * Grower pressing the canvas and expecting a shape to appear under the pointer.
   */
  onCirclePlaced?: (circle: FieldCircle) => void;
};

/**
 * The **Point**s a Field Mapping session has marked, and the one way to add to them.
 *
 * A candidate is plotted **only after its verdict** — there is no pending marker, because
 * "the point is not marked" is what the criteria say and a rolled-back optimistic marker would
 * flicker onto the map (ADR-0018).
 *
 * State is `{ points, history }` in a single `useState`: the immutable snapshot stack that makes Undo
 * `pop()` (ADR-0018), pushed by every commit site through `commit` below. Tickets 10/11 widen the
 * snapshot to `{ tool, points, circle }` — one stack for all of it, which is what lets one Undo step
 * back through **Point** actions and **Shape mode** switches in the order they happened.
 */
export function useFieldPoints(
  existingFields: readonly FieldTarget[],
  {
    excludeFieldId,
    initialPoints,
    initialCircle,
    initialMode = 'polygon',
    origin = 'drawn',
    onCirclePlaced,
  }: FieldPointsOptions,
) {
  // `points` and `history` live in ONE `useState`, not two, so every commit's history push reads the
  // pre-commit state from *inside* the same functional updater that computes the new state — the
  // same guarantee that already made the array safe when two marks are in flight at once. Two state
  // slots updated by two calls would let both in-flight commits push the same stale snapshot.
  const [state, setState] = useState<FieldPointsState>(() => ({
    tool: initialMode,
    points: [...(initialPoints ?? [])],
    circle: initialCircle ?? null,
    history: [],
  }));
  const { tool, points, circle, history } = state;
  const { mutateAsync } = useValidateBoundary();
  const toast = useToast();

  const held = useRef(new Set<number>());
  const cancelPendingMarks = useCallback(() => {
    held.current.forEach((id) => clearTimeout(id));
    held.current.clear();
  }, []);
  // A screen left mid-hold must not mark into an unmounted tree.
  useEffect(() => cancelPendingMarks, [cancelPendingMarks]);

  /** One place for every placement refusal, so a refused drag and a refused mark are told in the
   *  same tone, in the same slot, for the same 5 s (113600). */
  const refusePlacement = useCallback(
    (check: Extract<PlacementCheck, { ok: false }>) => {
      toast[check.tone]({
        title: check.message,
        key: MARKING_TOAST_KEY,
        duration: TOAST_DURATION_SECONDS,
      });
    },
    [toast],
  );

  /** The `checkPlacement` round trip both `addPoint` and `movePoint` run, differing only in the
   *  shape they send — shared here so a refused mark and a refused drag stay driven by one call. */
  const runPlacementCheck = useCallback(
    async (candidate: MapPoint, shape: readonly MapPoint[]) => {
      const check = await checkPlacement(
        mutateAsync,
        candidate,
        shape,
        existingFields,
        excludeFieldId,
      );
      if (check.ok) return true;
      refusePlacement(check);
      return false;
    },
    [mutateAsync, existingFields, excludeFieldId, refusePlacement],
  );

  const addPoint = useCallback(
    async (candidate: MapPoint) => {
      // Order matters: a click on an existing **Point** while already at the ceiling is the silent
      // no-op the criteria describe, not a cap refusal about a Point that was never being added.
      if (isDuplicate(candidate, points)) return;

      if (points.length >= MAX_POINTS_BY_ORIGIN[origin]) {
        toast.error({
          title: maxPointsMessage(origin),
          key: MARKING_TOAST_KEY,
          duration: TOAST_DURATION_SECONDS,
        });
        return;
      }

      if (!(await runPlacementCheck(candidate, [...points, candidate]))) return;
      // Functional, so two checks in flight at once both land. ponytail: the SECOND one's request
      // was built before the first resolved, so its ring verdict is one Point short — harmless at
      // human marking speed, and the hold above already collapses anything faster.
      //
      // **The cap is re-asserted here, against the live count.** The guard above reads `points` from
      // this render's closure; this updater reads `current.points`. With an await between them, two
      // marks fired against the same render both pass the ceiling check and then both commit — a
      // 201st Point on an imported shape, or a 51st on a drawn one, under a cap that reported itself
      // correctly — the drawn half of which was reproduced in manual QA by clicking the map fast.
      // `placeMidPoint` already re-checks inside its own updater for this exact reason.
      //
      // Keyed on `origin` like the guard, so the two halves cannot disagree about which ceiling
      // applies. PR #129 fixes the drawn case on `develop`; this is the same fix made origin-aware,
      // which is what makes THIS ticket's "the 201st Point is refused" criterion reliable.
      //
      // The dropped mark is **silent**. It was under the ceiling when it was admitted, so a refusal
      // naming the maximum would be about a Point the Grower watched land — that toast belongs to
      // the click refused up front, not to this one.
      setState((current) =>
        current.points.length >= MAX_POINTS_BY_ORIGIN[origin]
          ? current
          : commit(current, { points: [...current.points, candidate] }),
      );
    },
    [points, runPlacementCheck, toast, origin],
  );

  /**
   * Whether the tooltip offers a delete control at all (113600, grilling design-discrepancy **D7**:
   * at exactly three **Point**s the control is **absent**, not greyed). Derived from the count, so
   * the answer cannot drift from the shape.
   */
  const canDeletePoint = points.length > MIN_DRAWN_POINTS;

  /**
   * Remove one **Point**.
   *
   * "Joins the neighbours and renumbers every following Point" is `toSpliced` and nothing else: the
   * number a **Point** wears is its array index, so closing the gap in the array *is* the
   * renumbering, and the black highlight — also derived from position — follows for free. The same
   * holds at both ends: deleting **Point** 1 of 5 leaves the old **Point** 2 first, and therefore
   * joined to the last one when the ring closes.
   *
   * Guarded on the count, not on the caller: a tooltip with no delete control is the UI answer, and
   * this is the one that cannot be got around.
   */
  const deletePoint = useCallback((index: number) => {
    setState((current) =>
      // The floor is checked INSIDE the updater and returns `current` untouched, so a refused delete
      // leaves no history entry — an Undo press that undid nothing would be worse than a disabled one.
      current.points.length > MIN_DRAWN_POINTS
        ? commit(current, { points: current.points.toSpliced(index, 1) })
        : current,
    );
  }, []);

  /**
   * Reposition one **Point** — a drag's release, and the criteria's own exception to "plotted only
   * after its verdict": the drag has been visibly tracking the pointer all along, so what this
   * decides is whether the new position is *kept*.
   *
   * The same placement flow `addPoint` runs, which is why a refused drag says exactly what a refused
   * mark says. Two differences, both forced by what a move is:
   *
   * - the **Point** being moved is excluded from the duplicate check, so releasing a drag back where
   *   it began is a no-op rather than a refusal against its own coordinate;
   * - the shape sent is `with(index, candidate)`, the array as it would stand — not everything else
   *   plus the candidate, which would reorder the ring and get a verdict for a shape nobody drew.
   */
  const movePoint = useCallback(
    async (index: number, candidate: MapPoint) => {
      const others = points.filter((_, i) => i !== index);
      if (isDuplicate(candidate, others)) return;

      if (!(await runPlacementCheck(candidate, points.with(index, candidate)))) return;
      setState((current) => commit(current, { points: current.points.with(index, candidate) }));
    },
    [points, runPlacementCheck],
  );

  /**
   * Replace the committed shape with a whole typed one — the coordinate panel's own `Save`
   * (113601), and the **only** commit site that does not correspond to a single gesture.
   *
   * **One** history entry, not one per row: `commit` pushes the pre-change shape once, so Undo after
   * a panel Save returns to whatever the canvas held before it (ticket 08's criterion).
   *
   * One round trip, carrying the whole array. Every row was already pre-empted at blur, so what this
   * call is *for* is the **ring** verdict — the one thing no per-**Point** check can produce, and
   * without which a typed ring could enclose a Field that a clicked one could not. `at(-1)` is the
   * candidate only for the local pre-filter, which is optimistic and never authoritative
   * (ADR-0018).
   *
   * The 50-**Point** ceiling is not re-checked here: the panel cannot produce a 51st row, and a
   * second copy of the rule is a second place for it to drift.
   */
  const commitPoints = useCallback(
    async (next: readonly MapPoint[]) => {
      const last = next.at(-1);
      if (!last) return false;
      if (!(await runPlacementCheck(last, next))) return false;
      setState((current) => commit(current, { points: [...next] }));
      return true;
    },
    [runPlacementCheck],
  );

  /**
   * A click becomes the circle's **Mid Point**, and a circle appears around it at
   * {@link DEFAULT_CIRCLE_RADIUS_FT} (113601).
   *
   * **Only one Mid Point can be dropped**, and the guard is checked BEFORE the round trip — so a
   * click on a map that already carries a circle costs nothing and changes nothing, which is the
   * criterion exactly. Checked again inside the updater, because two held clicks can resolve
   * against the same pre-click state.
   *
   * What is validated is the whole default-radius **disc**, not the Mid Point on its own: a
   * Mid Point clear of every neighbour can still put 100 ft of circle across one.
   */
  const placeMidPoint = useCallback(
    async (candidate: MapPoint) => {
      if (circle) return;

      const check = await checkCirclePlacement(
        mutateAsync,
        { midPoint: candidate, radiusFt: DEFAULT_CIRCLE_RADIUS_FT },
        existingFields,
        excludeFieldId,
      );
      if (!check.ok) {
        refusePlacement(check);
        return;
      }

      // Captured from inside the updater rather than assumed from the guard above, so the camera
      // hears about a placement that ACTUALLY committed. The inner guard exists because two held
      // clicks can resolve against the same pre-click state; without this, the loser of that race
      // would still move the camera, to a Mid Point that is not the one on screen.
      //
      // Assigning out of an updater is a side effect, and StrictMode double-invokes updaters — safe
      // only because the assignment is idempotent: the same `candidate` and the same default Radius
      // produce the same circle both times.
      let placed: FieldCircle | null = null;
      setState((current) => {
        if (current.circle) return current;
        placed = { midPoint: candidate, radiusFt: DEFAULT_CIRCLE_RADIUS_FT };
        return commit(current, { circle: placed });
      });
      if (placed) onCirclePlaced?.(placed);
    },
    [circle, mutateAsync, existingFields, excludeFieldId, refusePlacement, onCirclePlaced],
  );

  /**
   * Commit a whole circle — **both** the handle drag's release and the panel's own `Save`, which is
   * why it takes the circle rather than a radius: typing can move the **Mid Point** and dragging
   * cannot, and one function that takes the result covers both without a flag.
   *
   * Validated as a whole (Radius range **and** the disc's full extent) before anything is committed.
   * On a refusal it commits **nothing** and returns `false` — which is the whole of "the circle
   * reverts to its last valid size": the page clears its live preview either way, so a refused drag
   * snaps back to the committed circle with no revert call at all.
   *
   * One history entry per commit, so a resize is one Undo step.
   */
  const commitCircle = useCallback(
    async (next: FieldCircle) => {
      const check = await checkCirclePlacement(mutateAsync, next, existingFields, excludeFieldId);
      if (!check.ok) {
        refusePlacement(check);
        return false;
      }
      // Unconditional, unlike `deleteCircle`'s guard: the panel's own Save is one of this
      // function's two callers, and it is exactly the case where NO circle exists yet — a
      // Mid Point and Radius typed with nothing clicked on the map. Gating on `current.circle`
      // would silently drop that commit.
      setState((current) => commit(current, { circle: next }));
      return true;
    },
    [mutateAsync, existingFields, excludeFieldId, refusePlacement],
  );

  /**
   * Remove the **Mid Point** and its circle (113601). **No minimum** — unlike `deletePoint`'s
   * 3-**Point** floor, because a circle is one shape rather than a count of corners, which is why
   * its tooltip's delete control is always available.
   *
   * Undo needs nothing new: this pushes the same pre-commit snapshot every other commit site
   * pushes, so the circle comes back with its own Mid Point and Radius.
   */
  const deleteCircle = useCallback(() => {
    setState((current) => (current.circle ? commit(current, { circle: null }) : current));
  }, []);

  /**
   * Change the **Shape mode** (113600/113601). Switching **clears the current shape**, which is why
   * this is a commit and not a `setMode`: the discarded geometry and the outgoing tool go onto the
   * same stack every other change uses, so ONE Undo puts back the **Point**s with their original
   * numbering and connections — or the **Mid Point** and **Radius** — AND reselects the tool that
   * drew them, in a single `pop()`.
   *
   * **Both** geometry axes are cleared, not just the outgoing one, so exactly one is ever non-empty
   * and `hasShape` below needs no `tool` branch of its own.
   *
   * Picking the tool that is already active commits nothing — the same reasoning as `deletePoint`'s
   * floor: an Undo press that undid nothing is worse than no entry at all.
   *
   * It does **not** confirm. Whether the Grower is asked first belongs to the screen, which owns the
   * dialog — and Undo has to be able to reverse a switch WITHOUT one (113600).
   */
  const switchMode = useCallback((next: FieldShapeType) => {
    setState((current) =>
      current.tool === next ? current : commit(current, { tool: next, points: [], circle: null }),
    );
  }, []);

  const markAt = useCallback(
    (candidate: MapPoint) => {
      const id = window.setTimeout(() => {
        held.current.delete(id);
        // The hold is the SAME in both modes — a double-click meant to zoom must leave neither a
        // **Point** nor a **Mid Point** behind — so only the destination differs.
        void (tool === 'circle' ? placeMidPoint(candidate) : addPoint(candidate));
      }, DOUBLE_CLICK_WINDOW_MS);
      held.current.add(id);
    },
    [tool, addPoint, placeMidPoint],
  );

  // Derived from the committed shape, never stored. Only one axis is ever non-empty, so this needs
  // no `mode` branch: a circle satisfies the gate on its own, and a polygon still owes three
  // **Point**s. A panel's own Save gate is a different, independent concern and both may be live at
  // once.
  const canSaveMapping = circle !== null || points.length >= MIN_DRAWN_POINTS;

  /** Whether a **Shape mode** switch would destroy anything — the "one or more **Point**s marked" /
   *  "a circle drawn" that the confirmations gate on (113601). Derived, and needs no `tool` branch:
   *  `switchMode` clears both axes, so only the active one is ever non-empty. */
  const hasShape = points.length > 0 || circle !== null;

  /** Why `Save Mapping` did nothing. Lives here rather than in the page so the key, the tone and
   *  the 5 s sit beside every other refusal in this feature and cannot drift from them. */
  const refuseSaveMapping = useCallback(() => {
    toast.error({
      title: MIN_POINTS_MESSAGE,
      key: SAVE_MAPPING_TOAST_KEY,
      duration: TOAST_DURATION_SECONDS,
    });
  }, [toast]);

  /**
   * 113600: "Undo reverses the most recent action", and repeated Undo steps back to the empty state.
   * A snapshot restore, never a computed reversal — which is what makes an undone deletion come back
   * with its original numbering and connections, and an undone move land wherever its shape was
   * before THAT action rather than wherever the pointer last was.
   *
   * The black highlight needs nothing here: `<MarkedPoints>` derives it as
   * `selectedIndex ?? points.length - 1`, so it moves to whichever **Point** is then most recent on
   * its own.
   */
  const undo = useCallback(() => {
    setState((current) => {
      const popped = popSnapshot(current.history);
      return popped ? { ...popped.snapshot, history: popped.rest } : current;
    });
  }, []);

  return {
    points,
    circle,
    /** The active **Shape mode**. Named `mode` for the screen (CONTEXT.md's word) and stored as
     *  `tool` in the snapshot (ADR-0018's `{ tool, points, circle }`). */
    mode: tool,
    switchMode,
    hasShape,
    markAt,
    cancelPendingMarks,
    canDeletePoint,
    deletePoint,
    movePoint,
    commitPoints,
    placeMidPoint,
    commitCircle,
    deleteCircle,
    canSaveMapping,
    refuseSaveMapping,
    undo,
    // Level 0 is an empty stack, and nothing else: the coordinate panel's **draft** (ticket 09) is
    // not in this history, so typing a row is not an undoable action.
    canUndo: history.length > 0,
  };
}
